import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MdBathtub,
  MdBed,
  MdCalendarToday,
  MdCheckCircle,
  MdFavorite,
  MdFavoriteBorder,
  MdHome,
  MdLayers,
  MdLocationOn,
  MdOutlineCancel,
  MdOutlineDrafts,
  MdOutlineSchedule,
  MdVerified,
} from 'react-icons/md';
import { Link } from 'react-router-dom';
import { formatRupiah } from '../../utils/currency';
import './DashboardPropertyCard.css';

const FAVORITES_STORAGE_KEY = 'mp_favorite_properties';
const FAVORITES_CHANGED_EVENT = 'mp-favorites-changed';

const APPROVAL_META = {
    draft: {
        label: 'Draft',
        icon: MdOutlineDrafts,
        className: 'pcard__badge--draft',
    },
    pending: {
        label: 'Pending',
        icon: MdOutlineSchedule,
        className: 'pcard__badge--pending',
    },
    approved: {
        label: 'Approved',
        icon: MdVerified,
        className: 'pcard__badge--approved',
    },
    rejected: {
        label: 'Rejected',
        icon: MdOutlineCancel,
        className: 'pcard__badge--rejected',
    },
};

const LISTING_META = {
    for_sale: { label: 'Dijual', className: 'pcard__badge--sale' },
    for_rent: { label: 'Disewakan', className: 'pcard__badge--rent' },
};

const INSPECTION_STATUS_LABEL = {
    pending: 'Menunggu inspeksi',
    scheduled: 'Jadwal ditentukan',
    in_progress: 'Sedang diinspeksi',
    completed: 'Selesai diinspeksi',
    cancelled: 'Dibatalkan',
    rejected: 'Ditolak',
};

const resolveImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === 'string') return image;
    return (
        image.image_url ||
        image.url ||
        image.path ||
        image.original_image_path ||
        image.file_url ||
        image.thumbnail_url ||
        null
    );
};

const getPropertyKey = (property) =>
    String(property?.id ?? property?.slug ?? property?.public_uuid ?? property?.title ?? '');

const getApprovalMeta = (status) => APPROVAL_META[String(status || '').toLowerCase()] ?? APPROVAL_META.draft;

const getListingMeta = (status) =>
    LISTING_META[String(status || '').toLowerCase()] ?? { label: status || '-', className: '' };

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        return new Date(dateStr).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return dateStr;
    }
};

const getStoredFavorites = () => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
        return raw ? (JSON.parse(raw) ?? []) : [];
    } catch {
        return [];
    }
};

const saveStoredFavorites = (favorites) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
        window.dispatchEvent(new Event(FAVORITES_CHANGED_EVENT));
    } catch {}
};

const normalizeFavoritePayload = (property, propertyKey, currentImage, priceText, approvalMeta, listingMeta) => ({
    key: propertyKey,
    id: property?.id ?? null,
    slug: property?.slug ?? null,
    public_uuid: property?.public_uuid ?? null,
    title: property?.title || '-',
    cover_image: currentImage || null,
    price: property?.price ?? null,
    price_display: priceText,
    status: property?.status ?? null,
    status_label: listingMeta.label,
    approval_status: property?.approval_status ?? null,
    approval_status_label: approvalMeta.label,
    city: property?.city || '',
    location: property?.location || '',
    saved_at: new Date().toISOString(),
});

const DashboardPropertyCard = ({ property, onFavoriteChange }) => {
    const propertyKey = useMemo(() => getPropertyKey(property), [property]);

    const imageList = useMemo(() => {
        const images = [];
        const push = (image) => {
            const url = resolveImageUrl(image);
            if (url && !images.includes(url)) images.push(url);
        };
        push(property?.image_url);
        if (Array.isArray(property?.images)) property.images.forEach(push);
        return images;
    }, [property?.image_url, property?.images]);

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isFavorite, setIsFavorite] = useState(false);

    const currentImage = imageList[currentImageIndex] || null;
    const priceText = property?.price_format || formatRupiah(property?.price);
    const approvalMeta = getApprovalMeta(property?.approval_status);
    const listingMeta = getListingMeta(property?.status);
    const ApprovalIcon = approvalMeta.icon;

    const inspection = property?.inspection ?? null;
    const inspectionStatus = inspection?.status ?? null;
    const canRequestInspection = inspection === null;

    const ownerName = property?.owner?.name || '-';

    useEffect(() => {
        setCurrentImageIndex(0);
    }, [propertyKey]);

    useEffect(() => {
        if (imageList.length <= 1) return undefined;
        const id = window.setInterval(() => setCurrentImageIndex((prev) => (prev + 1) % imageList.length), 3500);
        return () => window.clearInterval(id);
    }, [imageList]);

    useEffect(() => {
        if (typeof window === 'undefined' || !propertyKey) return undefined;
        const sync = () => {
            setIsFavorite(getStoredFavorites().some((item) => item?.key === propertyKey));
        };
        sync();
        window.addEventListener('storage', sync);
        window.addEventListener(FAVORITES_CHANGED_EVENT, sync);
        return () => {
            window.removeEventListener('storage', sync);
            window.removeEventListener(FAVORITES_CHANGED_EVENT, sync);
        };
    }, [propertyKey]);

    const handleToggleFavorite = useCallback(() => {
        if (!propertyKey) return;
        const favorites = getStoredFavorites();
        const exists = favorites.some((item) => item?.key === propertyKey);
        const next = exists
            ? favorites.filter((item) => item?.key !== propertyKey)
            : [
                  normalizeFavoritePayload(property, propertyKey, currentImage, priceText, approvalMeta, listingMeta),
                  ...favorites,
              ];
        saveStoredFavorites(next);
        setIsFavorite(!exists);
        onFavoriteChange?.(property, !exists);
    }, [approvalMeta, currentImage, listingMeta, onFavoriteChange, priceText, property, propertyKey]);

    return (
        <article className="pcard">
            <div className="pcard__media">
                {currentImage ? (
                    <img
                        key={currentImage}
                        src={currentImage}
                        alt={property?.title || 'Properti'}
                        className="pcard__image"
                        loading="lazy"
                    />
                ) : (
                    <div className="pcard__placeholder">
                        <MdHome size={32} />
                        <span>Tidak ada gambar</span>
                    </div>
                )}

                {currentImage && <div className="pcard__watermark">Urbania</div>}

                <div className="pcard__badges">
                    <span className={`pcard__badge ${approvalMeta.className}`}>
                        <ApprovalIcon size={10} />
                        {approvalMeta.label}
                    </span>
                    <span className={`pcard__badge ${listingMeta.className}`}>{listingMeta.label}</span>
                </div>

                <button
                    type="button"
                    className={`pcard__fav-btn${isFavorite ? ' is-active' : ''}`}
                    onClick={handleToggleFavorite}
                    aria-pressed={isFavorite}
                    aria-label={isFavorite ? 'Hapus dari favorit' : 'Simpan ke favorit'}
                    disabled={!propertyKey}
                >
                    {isFavorite ? <MdFavorite size={15} /> : <MdFavoriteBorder size={15} />}
                </button>

                {imageList.length > 1 && (
                    <>
                        <div className="pcard__dots">
                            {imageList.map((_, index) => (
                                <button
                                    key={`${propertyKey}-dot-${index}`}
                                    type="button"
                                    className={`pcard__dot${index === currentImageIndex ? ' is-active' : ''}`}
                                    onClick={() => setCurrentImageIndex(index)}
                                    aria-label={`Tampilkan gambar ${index + 1}`}
                                />
                            ))}
                        </div>
                        <div className="pcard__counter">
                            {currentImageIndex + 1}/{imageList.length}
                        </div>
                    </>
                )}
            </div>

            {/* ── Body ── */}
            <div className="pcard__body">
                <p className="pcard__title">{property?.title || '-'}</p>
                <p className="pcard__price">{priceText}</p>

                <div className="pcard__meta">
                    <MdHome size={12} />
                    <span>{property?.property_type?.name || property?.type || '-'}</span>
                    <span className="pcard__meta-dot" />
                    <MdLocationOn size={12} />
                    <span>{property?.city || property?.location || '-'}</span>
                </div>

                <div className="pcard__specs">
                    <span className="pcard__spec-chip">
                        <MdBed size={12} />
                        {property?.bedrooms ?? 0} KT
                    </span>
                    <span className="pcard__spec-chip">
                        <MdBathtub size={12} />
                        {property?.bathrooms ?? 0} KM
                    </span>
                    <span className="pcard__spec-chip">
                        <MdLayers size={12} />
                        {property?.floors ?? 0} Lt
                    </span>
                    {property?.certificate_type?.name && (
                        <span className="pcard__spec-chip">{property.certificate_type.name}</span>
                    )}
                </div>

                <div className="pcard__divider" />

                <div className="pcard__owner-row">
                    <div className="pcard__avatar">{getInitials(ownerName)}</div>
                    <span className="pcard__owner-name">
                        Pemilik: <strong>{ownerName}</strong>
                    </span>
                    <span className="pcard__date">
                        <MdCalendarToday size={10} />
                        {formatDate(property?.created_at)}
                    </span>
                </div>

                {/* Inspection */}
                <div className="pcard__actions">
                    {canRequestInspection ? (
                        <Link
                            to={`/dashboard/properties/${property?.id}/inspection-request`}
                            className="pcard__btn-inspection"
                        >
                            <MdOutlineSchedule size={14} />
                            Request inspeksi
                        </Link>
                    ) : inspectionStatus ? (
                        <div className="pcard__inspection-status">
                            <MdCheckCircle size={13} />
                            <span>
                                Inspeksi:{' '}
                                <strong>{INSPECTION_STATUS_LABEL[inspectionStatus] || inspectionStatus}</strong>
                            </span>
                        </div>
                    ) : null}

                    <div className="pcard__btn-row">
                        <Link to={`/dashboard/properties/${property?.id}`} className="pcard__btn pcard__btn--primary">
                            Lihat detail
                        </Link>
                        <Link to={`/dashboard/properties/${property?.id}/edit`} className="pcard__btn pcard__btn--ghost">
                            Ubah
                        </Link>
                    </div>
                </div>
            </div>
        </article>
    );
};

export default DashboardPropertyCard;
